import React from 'react';
import {
  HStack, Pressable, useMedia, VStack, Heading, View, Icon,
} from '@gluestack-ui/themed';
import { Modal } from 'react-native';
import { PlusIcon, X, LucideIcon } from 'lucide-react-native';
import { SafeAreaView } from 'common/components/SafeAreaView';
import log from '@/common/utils/logger';
import type { AdminObjectName, AdminModelMap, ColumnDefinition } from './DataColumnConfig';
import { DataTable } from './DataTable';

/**
 * Props for AddMemberModal component.
 *
 * Pattern: Uses dual generics <K, T> where:
 * - K = AdminObjectName (e.g., 'users', 'modules')
 * - T = Row type (defaults to AdminModelMap[K])
 *
 * This allows type-safe column definitions and callbacks.
 */
interface AddMemberModalProps<K extends AdminObjectName, T = AdminModelMap[K]> {
  visible?: boolean;
  hideModal: () => void;
  filter?: Record<string, unknown>;
  icons?: {
    add?: LucideIcon;
  };
  onAddMember: (id: string | number, options?: { onError?: (error: unknown) => void }) => Promise<void>;
  onError?: (error: unknown) => void;
  objectName: K;
  /** Column definitions with proper typing for render functions */
  columns: Record<string, ColumnDefinition<T>>;
  title?: string;
  defaultSearchField?: string;
  searchableFields?: string[];
  /** IDs to exclude from the add list (already added members) */
  excludeIds?: (string | number)[];
}

/**
 * AddMemberModal component for adding new members to a parent entity.
 *
 * @template K - AdminObjectName (e.g., 'users', 'modules')
 * @template T - Row type (defaults to AdminModelMap[K])
 *
 * ## Type Safety Pattern
 *
 * Use dual generics when your columns need extended types:
 *
 * ```typescript
 * const columns: Record<string, ColumnDefinition<CourseStudent>> = {
 *   lastname: {
 *     render: (row) => <Text>{row.lastname}, {row.firstname}</Text>,
 *   },
 * };
 *
 * <AddMemberModal<'users', CourseStudent>
 *   objectName="users"
 *   columns={columns}
 *   excludeIds={currentMemberIds}
 *   onAddMember={async (id) => await linkAPI(courseId, id)}
 * />
 * ```
 *
 * ## Features
 * - Searchable/filterable table
 * - Single and bulk add operations
 * - Automatic exclusion of already-added members
 * - Type-safe column definitions with proper render function inference
 *
 * @see ColumnDefinition for column configuration options
 */
export const AddMemberModal = <K extends AdminObjectName, T = AdminModelMap[K]>({
  visible = false,
  hideModal,
  filter = {},
  icons,
  onAddMember,
  onError,
  objectName,
  columns,
  title = 'Add Member',
  defaultSearchField,
  searchableFields,
  excludeIds = [],
}: AddMemberModalProps<K, T>) => {
  const height = 600;
  const media = useMedia();
  const [isActionRunning, setIsActionRunning] = React.useState(false);

  /**
   * Action configuration for adding members.
   * Type-safe actions that check if items are already excluded.
   */
  const addActions = React.useMemo(() => ({
    add: {
      label: 'Add',
      icon: icons?.add || PlusIcon,
      position: 'row' as const,
      availCondition: (row: T) => !excludeIds.map(String).includes(String((row as unknown as { id: string | number }).id)),
    },
    bulkAdd: {
      label: 'Add Selected',
      icon: icons?.add || PlusIcon,
      bulk: true,
    },
  }), [icons?.add, excludeIds]);

  /**
   * Handler for add member actions (single or bulk).
   * Type-safe with proper T[] handling.
   */
  const handleMemberAction = async (action: string, data: T | T[]) => {
    if (!data) {
      log.warn('AddMemberModal: data not provided');
      return;
    }

    const items = Array.isArray(data) ? data : [data];

    if (action === 'add' || action === 'bulkAdd') {
      setIsActionRunning(true);
      try {
        const promises = items
          .filter((item) => {
            const itemId = (item as unknown as { id?: string | number }).id;
            return !!itemId && !excludeIds.map(String).includes(String(itemId));
          })
          .map((item) => {
            const itemId = (item as unknown as { id: string | number }).id;
            return onAddMember(itemId, {
              onError: (error) => {
                if (onError) {
                  onError(error);
                } else {
                  log.error('Error adding member:', error);
                }
              },
            });
          });
        await Promise.all(promises);
        hideModal();
      } catch (error) {
        if (onError) {
          onError(error);
        } else {
          log.error('Error adding member:', error);
        }
      } finally {
        setIsActionRunning(false);
      }
    }
  };

  return (
    <Modal animationType="none" transparent visible={visible} onRequestClose={() => hideModal()}>
      <View
        flex={1}
        bg="rgba(0, 0, 0, 0.5)"
        justifyContent="center"
        alignItems="center"
      >
        <SafeAreaView
          $base-w="95%"
          $md-w={700}
          $lg-w={900}
          h={height - (media.md ? 2 * 24 : 2 * 62)}
          alignSelf="center"
          my={media.md ? 24 : 62}
          bg="$white"
          borderRadius={12}
          overflow="hidden"
        >
          <VStack p={24} maxHeight={height - (media.md ? 2 * 24 : 2 * 62)}>
            <HStack height={34} alignContent="center" justifyContent="space-between">
              <Heading size="xs" numberOfLines={1}>
                {title}
              </Heading>
              <Pressable testID="button-close-video-modal" onPress={() => hideModal()}>
                <Icon as={X} size="lg" />
              </Pressable>
            </HStack>

            <DataTable
              dataConfig={{
                objectName,
              }}
              displayConfig={{
                // Cast columns to base type for DataTable compatibility
                columns: columns as unknown as Record<string, ColumnDefinition<AdminModelMap[K]>>,
                noTitle: true,
              }}
              actionConfig={{
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                actions: addActions as any, // DataTable uses untyped action config internally
                onAction: handleMemberAction as (action: string, data: AdminModelMap[K] | AdminModelMap[K][]) => Promise<void>,
                isActionRunning,
              }}
              searchFilterConfig={{
                filter,
                defaultSearchField,
                searchableFields,
                showStateFilter: false,
                searchMaxWidth: 350,
                hideFilterLabel: true,
              }}
              paginationSelectionConfig={{
                pageSize: 7,
                enableSelection: true,
              }}
            />
          </VStack>
        </SafeAreaView>
      </View>
    </Modal>
  );
};

export default AddMemberModal;
